import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, type AuthResponse } from "../api";
import { setSessionToken } from "../auth";

/// Lands here from an emailed magic link (`/auth/magic?token=…`): redeems the
/// single-use token for a session, then bounces to the app. The token is
/// consumed on the server the first time, so we guard against React StrictMode's
/// double-mount firing verify twice (which would fail the second call).
export function MagicVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setError("missing");
      return;
    }
    void (async () => {
      try {
        const res = await api<AuthResponse>("/v1/auth/magic-link/verify", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        setSessionToken(res.sessionToken);
        navigate("/", { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : "failed");
      }
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center space-y-4">
        {error ? (
          <>
            <h1 className="text-xl font-semibold text-slate-800">Couldn't sign you in</h1>
            <p className="text-sm text-rose-600">{friendlyError(error)}</p>
            <a href="/login" className="text-sm text-brand-700 underline">
              Back to sign in
            </a>
          </>
        ) : (
          <p className="text-sm text-slate-500">Signing you in…</p>
        )}
      </div>
    </div>
  );
}

function friendlyError(code: string): string {
  switch (code) {
    case "missing":
      return "This sign-in link is missing its token. Request a new one.";
    case "invalid_or_expired":
      return "This link is invalid or has expired. Request a new one from the sign-in page.";
    case "not_invited":
      return "This email isn't set up as a coach yet. Ask your club admin for an invite.";
    default:
      return "This sign-in link didn't work. Request a new one.";
  }
}
